import { fetchAuthSession } from 'aws-amplify/auth';

import { HaygentMessage, HaygentSession, HaygentStreamChunk } from '../interfaces';
import { Config } from '../config';

export abstract class HaygentService {
  private static readonly baseUrl = `${Config.API}/haygent-api/v1`;

  /**
   * Gets the OIDC ID token from Cognito session.
   * Note: Uses idToken (not accessToken) as required by the Haygent backend.
   */
  private static getIdToken = async (): Promise<string> => {
    const session = await fetchAuthSession();

    const idToken = session?.tokens?.idToken?.toString();
    if (!idToken) {
      throw new Error('No authentication token');
    }

    return idToken;
  };

  /** Builds common headers with the Cognito identity used by the Haygent backend. */
  private static buildHeaders = async (extra: Record<string, string> = {}): Promise<Record<string, string>> => {
    const token = await HaygentService.getIdToken();

    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...extra,
    };
  };

  /**
   * POST /session creates a conversation owned by the user identified by the OIDC ID token.
   * Session creation stays lazy: callers invoke this only when the first message is sent.
   */
  public static createSession = async (): Promise<HaygentSession> => {
    const response = await fetch(`${HaygentService.baseUrl}/session`, {
      method: 'POST',
      headers: await HaygentService.buildHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to create session: ${response.status}`);
    }

    return response.json();
  };

  /**
   * GET /sessions returns every conversation owned by the authenticated OIDC subject.
   * User scoping is enforced by the Haygent backend, not by client-side filtering.
   */
  public static listSessions = async (): Promise<HaygentSession[]> => {
    const response = await fetch(`${HaygentService.baseUrl}/sessions`, {
      method: 'GET',
      headers: await HaygentService.buildHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to list sessions: ${response.status}`);
    }

    const sessions: HaygentSession[] = await response.json();

    // ISO UTC timestamps sort lexicographically and retain microseconds that JavaScript Date would truncate.
    return [...sessions].sort((firstSession, secondSession) => secondSession.created_at.localeCompare(firstSession.created_at));
  };

  /**
   * GET /chatbot/messages returns persisted messages for the session selected by X-Session-Id.
   * System messages are backend context and are intentionally omitted from the visible history.
   */
  public static getSessionMessages = async (sessionId: string): Promise<HaygentMessage[]> => {
    const response = await fetch(`${HaygentService.baseUrl}/chatbot/messages`, {
      method: 'GET',
      headers: await HaygentService.buildHeaders({
        'X-Session-Id': sessionId,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to get session messages: ${response.status}`);
    }

    const data: { messages: Array<{ role: string; content: string }> } = await response.json();

    return data.messages.filter((message): message is HaygentMessage => message.role === 'user' || message.role === 'assistant');
  };

  /**
   * DELETE /session/{sessionId} permanently deletes the selected conversation.
   * The backend contract requires the session ID in both the path and X-Session-Id header.
   */
  public static deleteSession = async (sessionId: string): Promise<void> => {
    const response = await fetch(`${HaygentService.baseUrl}/session/${encodeURIComponent(sessionId)}`, {
      method: 'DELETE',
      headers: await HaygentService.buildHeaders({
        'X-Session-Id': sessionId,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to delete session: ${response.status}`);
    }
  };

  /**
   * POST /chatbot/chat/stream sends the conversation messages for the session in X-Session-Id.
   * The response is consumed as SSE and emitted incrementally through onChunk until done is true.
   */
  public static streamChat = async (
    sessionId: string,
    messages: HaygentMessage[],
    onChunk: (chunk: HaygentStreamChunk) => void,
    abortSignal?: AbortSignal,
    fleetUuid?: string,
  ): Promise<void> => {
    const response = await fetch(`${HaygentService.baseUrl}/chatbot/chat/stream`, {
      method: 'POST',
      headers: await HaygentService.buildHeaders({
        'X-Session-Id': sessionId,
        Accept: 'text/event-stream',
        ...(fleetUuid && { 'X-Fleet-Uuid': fleetUuid }),
      }),
      body: JSON.stringify({ messages }),
      signal: abortSignal,
    });

    if (!response.ok) {
      const error = new Error(`Chat request failed: ${response.status}`) as Error & { status: number };
      error.status = response.status;
      throw error;
    }

    if (!response.body) {
      throw new Error('No response body');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let streamComplete = false;

    const processChunks = async (): Promise<void> => {
      if (streamComplete) {
        return;
      }

      const { value, done } = await reader.read();
      if (done) {
        return;
      }

      buffer += decoder.decode(value, { stream: true });
      const parts = buffer.split('\n\n');
      buffer = parts.pop() || '';

      parts.forEach((part) => {
        const line = part.split('\n').find((l) => l.startsWith('data:'));
        if (line) {
          try {
            const payload: HaygentStreamChunk = JSON.parse(line.replace(/^data:\s?/, ''));
            onChunk(payload);
            if (payload.done) {
              streamComplete = true;
              // Proactively close the stream to avoid leaving the SSE connection open
              // eslint-disable-next-line @typescript-eslint/no-floating-promises
              reader.cancel();
            }
          } catch {
            // Skip malformed JSON
          }
        }
      });

      if (!streamComplete) {
        await processChunks();
      }
    };

    await processChunks();
  };
}
